/**
 * Showcase-only constants that are not exported from @enamelai/design-system 1.2.1.
 * Kept local to the /ui-ds catalog so the package API stays lean.
 */
import type {
  BreadcrumbItem,
  ButtonSize,
  LinkButtonSize,
} from '@enamelai/design-system'

export type ButtonPreviewState =
  | 'default'
  | 'hover'
  | 'pressed'
  | 'focused'
  | 'disabled'
  | 'loading'

export type ButtonShowcaseVariantGroup = {
  id: string
  label: string
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'text'
  destructive?: boolean
}

export const BUTTON_SIZES: ButtonSize[] = ['xs', 'sm', 'md', 'lg']

export const BUTTON_SIZE_LABELS: Record<ButtonSize, string> = {
  xs: 'XS',
  sm: 'S',
  md: 'M',
  lg: 'L',
}

export const BUTTON_PREVIEW_STATES: ButtonPreviewState[] = [
  'default',
  'hover',
  'pressed',
  'focused',
  'disabled',
  'loading',
]

export const BUTTON_PREVIEW_STATE_LABELS: Record<ButtonPreviewState, string> = {
  default: 'Default',
  hover: 'Hover',
  pressed: 'Pressed',
  focused: 'Focused',
  disabled: 'Disabled',
  loading: 'Loading',
}

export const BUTTON_SHOWCASE_VARIANT_GROUPS: ButtonShowcaseVariantGroup[] = [
  { id: 'primary', label: 'Primary', variant: 'primary' },
  {
    id: 'primary-destructive',
    label: 'Primary destructive',
    variant: 'primary',
    destructive: true,
  },
  { id: 'secondary', label: 'Secondary', variant: 'secondary' },
  {
    id: 'secondary-destructive',
    label: 'Secondary destructive',
    variant: 'secondary',
    destructive: true,
  },
  { id: 'tertiary', label: 'Tertiary', variant: 'tertiary' },
  { id: 'ghost', label: 'Ghost', variant: 'ghost' },
  { id: 'text', label: 'Text', variant: 'text' },
]

export type LinkButtonPreviewState =
  | 'default'
  | 'hover'
  | 'pressed'
  | 'focused'
  | 'disabled'

export const LINK_BUTTON_SIZES: LinkButtonSize[] = ['sm', 'md', 'lg']

export const LINK_BUTTON_PREVIEW_STATES: LinkButtonPreviewState[] = [
  'default',
  'hover',
  'pressed',
  'focused',
  'disabled',
]

export const LINK_BUTTON_SIZE_LABELS: Record<LinkButtonSize, string> = {
  sm: 'S',
  md: 'M',
  lg: 'L',
}

export const LINK_BUTTON_PREVIEW_STATE_LABELS: Record<
  LinkButtonPreviewState,
  string
> = {
  default: 'Default',
  hover: 'Hover',
  pressed: 'Pressed',
  focused: 'Focused',
  disabled: 'Disabled',
}

export type CheckboxPreviewState = 'default' | 'hover' | 'focused' | 'disabled'

export const CHECKBOX_PREVIEW_STATES: CheckboxPreviewState[] = [
  'default',
  'hover',
  'focused',
  'disabled',
]

export const CHECKBOX_PREVIEW_STATE_LABELS: Record<
  CheckboxPreviewState,
  string
> = {
  default: 'Default',
  hover: 'Hover',
  focused: 'Focused',
  disabled: 'Disabled',
}

export type RadioPreviewState = 'default' | 'hover' | 'focused' | 'disabled'

export const RADIO_PREVIEW_STATES: RadioPreviewState[] = [
  'default',
  'hover',
  'focused',
  'disabled',
]

export const RADIO_PREVIEW_STATE_LABELS: Record<RadioPreviewState, string> = {
  default: 'Default',
  hover: 'Hover',
  focused: 'Focused',
  disabled: 'Disabled',
}

export type ToggleSwitchPreviewState =
  | 'default'
  | 'hover'
  | 'focused'
  | 'disabled'

export const TOGGLE_SWITCH_PREVIEW_STATES: ToggleSwitchPreviewState[] = [
  'default',
  'hover',
  'focused',
  'disabled',
]

export const TOGGLE_SWITCH_PREVIEW_STATE_LABELS: Record<
  ToggleSwitchPreviewState,
  string
> = {
  default: 'Default',
  hover: 'Hover',
  focused: 'Focused',
  disabled: 'Disabled',
}

export type EmptyStatePreviewState = 'default' | 'with-action' | 'no-action'

export const EMPTY_STATE_PREVIEW_STATES: EmptyStatePreviewState[] = [
  'default',
  'with-action',
  'no-action',
]

export const EMPTY_STATE_PREVIEW_STATE_LABELS: Record<
  EmptyStatePreviewState,
  string
> = {
  default: 'Default',
  'with-action': 'With action',
  'no-action': 'No action',
}

export type StepperPreviewStep = 1 | 2 | 3 | 4

export const STEPPER_PREVIEW_STEPS: StepperPreviewStep[] = [1, 2, 3, 4]

export const STEPPER_PREVIEW_STEP_LABELS: Record<StepperPreviewStep, string> = {
  1: 'Step 1',
  2: 'Step 2',
  3: 'Step 3',
  4: 'Step 4',
}

export type TabsPreviewCount = 2 | 3 | 4

export const TABS_PREVIEW_COUNTS: TabsPreviewCount[] = [2, 3, 4]

export const TABS_PREVIEW_COUNT_LABELS: Record<TabsPreviewCount, string> = {
  2: '2 tabs',
  3: '3 tabs',
  4: '4 tabs',
}

export const BREADCRUMB_SHOWCASE_PRESETS = [
  'default',
  'multiple',
  'long',
] as const

export type BreadcrumbShowcasePreset =
  (typeof BREADCRUMB_SHOWCASE_PRESETS)[number]

export const buildBreadcrumbPresetItems = (
  preset: BreadcrumbShowcasePreset,
): BreadcrumbItem[] => {
  switch (preset) {
    case 'default':
      return [{ label: 'Item 1' }, { label: 'Item 2' }]
    case 'multiple':
      return [{ label: 'Item 1' }, { label: 'Item 2' }, { label: 'Item 3' }]
    case 'long':
      return Array.from({ length: 10 }, (_, index) => ({
        id: `item-${index + 1}`,
        label: index === 9 ? 'Item10' : `Item ${index + 1}`,
      }))
    default:
      return [{ label: 'Item 1' }, { label: 'Item 2' }]
  }
}
